import { Button } from 'primereact/button';
import { useEffect, useState } from 'react';
import { makeRequest2 } from '../../../utils/makeRequest';
import { toastify } from '../../../utils/toast';
import {
    addBankDetailsURL,
    deleteBankDetailsURL,
    getBankDetailsURL,
    updateBankDetailsURL
} from '../../../utils/urls';

const BankDetailsTab = ({ facilityId }) => {
    const [bankDetails, setBankDetails] = useState({
        accountName: '',
        accountNumber: '',
        bankName: '',
        branchName: '',
        branchCode: '',
        bankCode: '',
        isDefault: false
    });

    const [bankDetailsList, setBankDetailsList] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (facilityId) {
            console.log('Component mounted with facilityId:', facilityId);
            fetchBankDetails();
        } else {
            console.log('No facilityId provided');
        }
    }, [facilityId]);

    const fetchBankDetails = async () => {
        try {
            console.log('Fetching bank details from:', `${getBankDetailsURL}/${facilityId}`);
            const response = await makeRequest2(`${getBankDetailsURL}/${facilityId}`, 'GET');

            console.log('Raw response:', response);

            if (response && response.success && response.data) {
                console.log('Bank details data:', response.data);
                console.log('Data type:', typeof response.data);
                console.log('Is array:', Array.isArray(response.data));

                // Handle both array and object responses
                let details = [];
                if (Array.isArray(response.data)) {
                    details = response.data;
                } else if (typeof response.data === 'object' && response.data !== null) {
                    // If it's an object, try to extract array from common properties
                    if (response.data.data && Array.isArray(response.data.data)) {
                        details = response.data.data;
                    } else if (response.data.bankDetails && Array.isArray(response.data.bankDetails)) {
                        details = response.data.bankDetails;
                    } else {
                        // If it's a single object, wrap it in an array
                        details = [response.data];
                    }
                }

                console.log('Final bank details array:', details);
                setBankDetailsList(details);
            } else if (response && response.data) {
                // Fallback: if response.data exists but success is not explicitly true
                console.log('Using fallback data structure:', response.data);
                console.log('Fallback data type:', typeof response.data);

                let details = [];
                if (Array.isArray(response.data)) {
                    details = response.data;
                } else if (typeof response.data === 'object' && response.data !== null) {
                    details = [response.data];
                }

                setBankDetailsList(details);
            } else {
                console.log('No valid response data structure found:', response);
                setBankDetailsList([]);
            }
        } catch (error) {
            console.error('Error fetching bank details:', error);
            console.error('Error response:', error.response?.data);
            toastify('Failed to fetch bank details', 'error');
            setBankDetailsList([]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const url = isEditing
                ? `${updateBankDetailsURL}/${facilityId}/${bankDetails._id}`
                : `${addBankDetailsURL}/${facilityId}`;

            const method = isEditing ? 'PUT' : 'POST';

            console.log('Submitting bank details to:', url, 'Method:', method);
            console.log('Facility ID:', facilityId);

            const response = await makeRequest2(url, method, bankDetails);

            console.log('Response:', response);
            console.log('Response type:', typeof response);
            console.log('Response success:', response?.success);
            console.log('Response message:', response?.message);
            console.log('Response error:', response?.error);

            if (response && (response.success || response.message)) {
                toastify(`Bank details ${isEditing ? 'updated' : 'added'} successfully`, 'success');
                fetchBankDetails();
                resetForm();
            } else {
                const errorMessage = response?.error || response?.message || 'Operation failed';
                console.error('API Error:', response);
                toastify(errorMessage, 'error');
            }
        } catch (error) {
            console.error('Request Error:', error);
            console.error('Error details:', error.response?.data || error.message);

            // Check if it's a network error or response error
            if (error.response) {
                // Server responded with error status
                const errorMessage = error.response.data?.error || error.response.data?.message || `Server error: ${error.response.status}`;
                toastify(errorMessage, 'error');
            } else if (error.request) {
                // Request was made but no response received
                toastify('Network error: Unable to reach server', 'error');
            } else {
                // Something else happened
                toastify('Error saving bank details', 'error');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (details) => {
        setBankDetails({
            ...details
        });
        setIsEditing(true);
    };

    const handleDelete = async (detailsId) => {
        if (window.confirm('Are you sure you want to delete these bank details?')) {
            try {
                console.log('Deleting bank details:', `${deleteBankDetailsURL}/${facilityId}/${detailsId}`);
                const response = await makeRequest2(`${deleteBankDetailsURL}/${facilityId}/${detailsId}`, 'DELETE');

                if (response && (response.success || response.message)) {
                    toastify('Bank details deleted successfully', 'success');
                    fetchBankDetails();
                } else {
                    toastify(response?.message || 'Delete failed', 'error');
                }
            } catch (error) {
                console.error('Error deleting bank details:', error);
                toastify('Error deleting bank details', 'error');
            }
        }
    };

    const resetForm = () => {
        setBankDetails({
            accountName: '',
            accountNumber: '',
            bankName: '',
            branchName: '',
            branchCode: '',
            bankCode: '',
            isDefault: false
        });
        setIsEditing(false);
    };

    return (
        <div className="row p-3">
            <div className="col-lg-6">
                <div className="card">
                    <div className="card-body">
                        <h5>{isEditing ? 'Update' : 'Add'} Bank Details</h5>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group mt-3">
                                <label>Account Name*</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={bankDetails.accountName}
                                    onChange={(e) => setBankDetails({ ...bankDetails, accountName: e.target.value })}
                                    placeholder="Account holder name"
                                    required
                                />
                            </div>

                            <div className="form-group mt-3">
                                <label>Account Number*</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={bankDetails.accountNumber}
                                    onChange={(e) => setBankDetails({ ...bankDetails, accountNumber: e.target.value })}
                                    placeholder="1234567890"
                                    required
                                />
                            </div>

                            <div className="form-group mt-3">
                                <label>Bank Name*</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={bankDetails.bankName}
                                    onChange={(e) => setBankDetails({ ...bankDetails, bankName: e.target.value })}
                                    placeholder="e.g., Equity Bank"
                                    required
                                />
                            </div>

                            <div className="form-group mt-3">
                                <label>Branch Name*</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={bankDetails.branchName}
                                    onChange={(e) => setBankDetails({ ...bankDetails, branchName: e.target.value })}
                                    placeholder="e.g., Westlands Branch"
                                    required
                                />
                            </div>

                            <div className="row">
                                <div className="col-md-6">
                                    <div className="form-group mt-3">
                                        <label>Branch Code</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={bankDetails.branchCode}
                                            onChange={(e) => setBankDetails({ ...bankDetails, branchCode: e.target.value })}
                                            placeholder="Branch code"
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group mt-3">
                                        <label>Bank Code</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={bankDetails.bankCode}
                                            onChange={(e) => setBankDetails({ ...bankDetails, bankCode: e.target.value })}
                                            placeholder="Bank code"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-check mt-3">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={bankDetails.isDefault}
                                    onChange={(e) => setBankDetails({ ...bankDetails, isDefault: e.target.checked })}
                                    id="defaultAccount"
                                />
                                <label className="form-check-label" htmlFor="defaultAccount">
                                    Set as Default Bank Account
                                </label>
                            </div>

                            <div className="mt-4 text-end">
                                {isEditing && (
                                    <button
                                        type="button"
                                        className="btn btn-secondary me-2"
                                        onClick={resetForm}
                                        disabled={loading}
                                    >
                                        Cancel
                                    </button>
                                )}
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? 'Saving...' : (isEditing ? 'Update' : 'Add')} Bank Details
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <div className="col-lg-6">
                <div className="card">
                    <div className="card-body">
                        <h5>Bank Details</h5>
                        <div className="table-responsive">
                            <table className="table align-middle">
                                <thead>
                                    <tr>
                                        <th>Account Name</th>
                                        <th>Bank</th>
                                        <th>Account Number</th>
                                        <th>Default</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bankDetailsList.length > 0 ? (
                                        bankDetailsList.map((details) => (
                                            <tr key={details._id}>
                                                <td>{details.accountName}</td>
                                                <td>
                                                    <div>
                                                        <div className="fw-bold">{details.bankName}</div>
                                                        <small className="text-muted">{details.branchName}</small>
                                                    </div>
                                                </td>
                                                <td>{details.accountNumber}</td>
                                                <td>
                                                    {details.isDefault ? (
                                                        <span className="badge bg-success">Yes</span>
                                                    ) : (
                                                        <span className="badge bg-secondary">No</span>
                                                    )}
                                                </td>
                                                <td className="text-end">
                                                    <Button
                                                        icon="ti ti-pencil"
                                                        className="p-button-rounded p-button-warning me-2"
                                                        size="small"
                                                        onClick={() => handleEdit(details)}
                                                        tooltip="Edit"
                                                    />
                                                    <Button
                                                        icon="ti ti-trash"
                                                        className="p-button-rounded p-button-danger"
                                                        size="small"
                                                        onClick={() => handleDelete(details._id)}
                                                        tooltip="Delete"
                                                    />
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="text-center py-4">
                                                <div className="text-muted">
                                                    <i className="ti ti-inbox mb-2" style={{ fontSize: "24px" }}></i>
                                                    <p className="mb-0">No bank details available</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BankDetailsTab;